import { Coin } from '../icons/Coin.tsx'
import { useState, useEffect, useMemo, useRef, useCallback, type CSSProperties } from 'react'
import useSEO from '../hooks/useSEO'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useOpenAuth } from '../hooks/useAuthModal'
import { useAuth } from '../context/AuthContext'
import api from '../api/client'
import { QRCodeSVG } from 'qrcode.react'
import { markPaymentStart } from '../utils/paymentReturn'
import { openTopUp } from '../utils/topUpRequest'
import {useMediaQuery} from '../hooks/useMediaQuery'
import { reachGoal } from '../utils/ym'
import TariffCardView from '../components/TariffCardView'
import { carryoverBenefit, formatCredits } from '../data/tariffShowcase'
import type { Tariff, TariffPack } from '../data/tariffShowcase'
import PromoCodeField from '../components/PromoCodeField'
import { usePromoCode } from '../hooks/usePromoCode'
import { clearStoredPromo, type PromoPurchase } from '../api/promo'

// Витринные типы и хелперы подачи (Tariff, formatPrice, …) живут в
// data/tariffShowcase — их делят витрина, TariffCardView и биллинг ЛК.

interface PaymentCreateResponse {
  payment_url: string
  order_id: string
  // Итог к оплате — уже со скидкой по промокоду
  amount: number
  credits: number
  bonus_credits?: number
  discount?: number
}

interface PaymentStatusResponse {
  order_id: string
  status: string
  amount: number
  credits: number
  created_at: string
  completed_at: string | null
}

type ModalStep = 'confirm' | 'change' | 'loading' | 'qr' | 'success' | 'error' | 'timeout' | 'paid_period'

// Тип покупки тарифа: подписка (автопродление, перенос остатка) или разовый
// платёж (без продления, остаток сгорает в конце периода). Условия разные —
// юзер выбирает явно, «тихой» разницы больше нет.
type PayType = 'subscription' | 'one_time'

// Дата с сервера — UTC без зоны (datetime.utcnow().isoformat()): читаем как UTC, а не как местное время.
const parseUtc = (iso: string) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
// «9 ноября»; другой год — «9 ноября 2027»
const formatPaidUntil = (iso: string) => {
  const d = parseUtc(iso)
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) })
}

export default function Tariffs() {

  useSEO({
    title: 'Тарифы Umnik.AI — нейросети от 190₽ за пакет',
    description: 'Цены на нейросети Umnik.AI. Кредитная система — платите только за использование. Оплата российской картой и через СБП. ChatGPT, Midjourney, Nano Banana и 100+ моделей.',
    canonical: 'https://umnik.ai/tariffs',
  })
  
  const { user, refreshUser } = useAuth()
  const navigate = useNavigate()
  // Регистрация/вход гостя — окном над тарифами (useOpenAuth): после входа шаг назад возвращает к тарифам, ссылка
  // из письма — на /tariffs (дозавершить оплату).
  const openAuth = useOpenAuth()
  const [searchParams] = useSearchParams()
  const [tariffs, setTariffs] = useState<Tariff[]>([])
  // Бонус-паки (рейл): бэк отдаёт их только суперадмину до запуска — пустой
  // список просто прячет рейл. Выбор НЕ предустановлен: подсветка «своего»
  // пака у владельца вариации — отражение факта, не предвыбор оплаты.
  const [packs, setPacks] = useState<TariffPack[]>([])
  const [selectedPack, setSelectedPack] = useState<string | null>(null)
  const packInitRef = useRef(false)
  const [loading, setLoading] = useState(true)
  const [selectedTariff, setSelectedTariff] = useState<Tariff | null>(null)
  const [modalStep, setModalStep] = useState<ModalStep>('confirm')
  const [paymentUrl, setPaymentUrl] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [creditsAdded, setCreditsAdded] = useState(0)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollingStartRef = useRef<number>(0)

  const isMobile = useMediaQuery("(max-width: 990px)");
  const [openTariffIds, setOpenTariffIds] = useState<Set<string>>(() => new Set());
  const [startUsed, setStartUsed] = useState(false)
  const [currentTariffId, setCurrentTariffId] = useState<string | null>(null)
  const [payType, setPayType] = useState<PayType>('one_time')
  const [recurrentEnabled, setRecurrentEnabled] = useState(false)
  // ФЗ-376: явное согласие на автосписание — отдельный чекбокс, ПУСТОЙ по
  // умолчанию. Без галочки кнопка «Оформить подписку» недоступна. Сбрасывается
  // при закрытии/смене тарифа/переключении тумблера — согласие не «запоминается».
  const [subConsent, setSubConsent] = useState(false)
  // Смена подписки (POST /subscription/change) — ошибка живёт inline в шаге
  // 'change', не роняя юзера на общий error-экран
  const [changeBusy, setChangeBusy] = useState(false)
  const [changeError, setChangeError] = useState('')
  // Гард от повторного нажатия на кнопку оплаты: пока создаётся платёж, кнопка
  // disabled. Без него быстрый двойной клик успевал отправить два POST /create
  // до перерисовки модалки (два платежа / две подписки).
  const [paying, setPaying] = useState(false)
  // Сумма созданного платежа (со скидкой) — для QR-экрана
  const [payAmount, setPayAmount] = useState<number | null>(null)

  const hasActiveSubscription = user?.subscription_status === 'active' || user?.subscription_status === 'trial'

  // Промокод проверяется на ту покупку, что выбрана в модалке: подписка
  // (скидка — только на первый платёж) или разовый тариф. Смена тарифа
  // подписки промокоды не принимает — там поля нет и проверок тоже.
  const promoPurchase = useMemo<PromoPurchase | null>(() => {
    if (!user || !selectedTariff || modalStep === 'change') return null
    return {
      purchase_kind: recurrentEnabled && payType === 'subscription' ? 'subscription' : 'tariff_one_time',
      tariff_id: selectedTariff.id,
    }
  }, [user, selectedTariff, modalStep, recurrentEnabled, payType])
  const promo = usePromoCode(promoPurchase, { onActivated: () => refreshUser() })
  const promoQuote = promo.quote

  useEffect(() => {
    // Гостю нечего освежать: 401 от /user/me выкидывал его на /login
    // (hard-redirect клиента) — тарифы были невидимы гостям и поисковикам.
    if (user) refreshUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Тариф «только подписка»: если по какой-то причине payType остался разовым
  // (deep-link, гонка конфигов) — принудительно возвращаем подписку.
  useEffect(() => {
    if (!selectedTariff) return
    const mode = selectedTariff.purchase_mode ?? 'both'
    if (mode === 'subscription' && payType !== 'subscription' && recurrentEnabled) setPayType('subscription')
    if (mode === 'one_time' && payType !== 'one_time') setPayType('one_time')
  }, [selectedTariff, payType, recurrentEnabled])

  useEffect(() => {
    const creditsParam = searchParams.get('credits')
    if (!creditsParam || tariffs.length === 0) return
    const target = tariffs.find((t) => String(t.credits) === creditsParam)
    if (target) {
      setSelectedTariff(target)
      // Подписчику deep-link открывает смену подписки, не разовую покупку
      if (hasActiveSubscription) {
        setChangeError('')
        setModalStep('change')
      } else {
        // Первый экран всегда открывается на подписке (единый оффер)
        setPayType(recurrentEnabled ? 'subscription' : 'one_time')
        setModalStep('confirm')
        promo.reset()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tariffs])

  useEffect(() => {
    if (!isMobile) setOpenTariffIds(new Set());
  }, [isMobile]);

  const loadConfig = async () => {
    try {
      const res = await api.request<{recurrent_enabled: boolean}>('/api/v1/payments/config')
      setRecurrentEnabled(Boolean(res.recurrent_enabled))
    } catch { /* сбой не критичен — остаёмся на дефолте */ }
  }

  const loadSubscription = async () => {
    try {
      const res = await api.request<{start_used: boolean; tariff_id: string | null}>('/api/v1/payments/subscription')
      setStartUsed(res.start_used)
      setCurrentTariffId(res.tariff_id || null)
    } catch { /* сбой не критичен — остаёмся на дефолте */ }
  }

  const loadTariffs = async (authed: boolean) => {
    try {
      // Гость — по-прежнему skipAuth (публичный эндпоинт; протухший токен не
      // уронит страницу в 401-редирект клиента). Залогиненный шлёт токен:
      // бэк по роли решает, отдавать ли витринные блоки (мягкий запуск —
      // пока только суперадмину). На бэке auth опциональный, 401 не вернёт.
      const res = await api.request<Tariff[]>('/api/v1/tariffs', { skipAuth: !authed })
      setTariffs(res)
      try {
        const pk = await api.request<TariffPack[]>('/api/v1/tariffs/packs', { skipAuth: !authed })
        setPacks(Array.isArray(pk) ? pk : [])
      } catch { setPacks([]) /* старый бэк — рейла нет */ }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    loadTariffs(Boolean(user))
    // /payments/config ПУБЛИЧНЫЙ — рекуррент-флаг нужен и гостю: без него
    // модал показывал только разовую оплату, пряча сегмент «Подписка /
    // Разовая» (решение владельца 2026-08-08 — оффер одинаков для всех).
    // Приватным остаётся /payments/subscription: гостя 401 от него делал
    // hard-redirect на /login, и страница была невидима поисковикам.
    loadConfig()
    if (user) {
      loadSubscription()
    }
    return () => stopPolling()
  }, [user, stopPolling])


  const startPolling = useCallback((oid: string) => {
    stopPolling()
    pollingStartRef.current = Date.now()
    pollingRef.current = setInterval(async () => {
      const elapsed = Date.now() - pollingStartRef.current
      if (elapsed > 300000) {
        stopPolling()
        setModalStep('timeout')
        return
      }
      try {
        const res = await api.request<PaymentStatusResponse>(`/api/v1/payments/status/${oid}`)
        if (res.status === 'completed') {
          stopPolling()
          setCreditsAdded(res.credits)
          setModalStep('success')
          refreshUser()
        } else if (res.status === 'cancelled' || res.status === 'expired' || res.status === 'failed') {
          stopPolling()
          setErrorMsg(
            res.status === 'cancelled'
              ? 'Платёж отклонён. Попробуйте другой способ оплаты.'
              : 'Время оплаты истекло. Попробуйте снова.'
          )
          setModalStep('error')
        }
      } catch { /* ignore */ }
    }, 3000)
  }, [stopPolling, refreshUser])

  // Ссылка СБП (qr.nspk.ru — разовая оплата, sub.nspk.ru — привязка счёта по
  // подписке) читается камерой банка, поэтому рисуется QR-кодом у нас.
  const isNspk = (url: string) => /nspk\.ru/i.test(url || '')

  const handleSelect = (tariff: Tariff) => {
    if (tariff.price === 0) {
      if (user) {
        navigate('/account')
      } else {
        openAuth('/register')
      }
      return
    }
    setSelectedTariff(tariff)
    // Подписчик меняет ПОДПИСКУ (не покупает пакет): отдельный шаг с
    // объяснением замены; разовая покупка ему не предлагается — для токенов
    // есть «Докупить токены» в кабинете.
    if (hasActiveSubscription) {
      setChangeError('')
      setModalStep('change')
      return
    }
    // Тумблер по умолчанию на подписке (подача), но согласие ПУСТОЕ —
    // без галочки оплатить нельзя. Режим продажи тарифа сужает выбор.
    const mode = tariff.purchase_mode ?? 'both'
    setPayType(mode === 'one_time' ? 'one_time' : recurrentEnabled ? 'subscription' : 'one_time')
    setSubConsent(false)
    setModalStep('confirm')
    // Код из ссылки ?promo= — сразу в поле и на проверку
    promo.reset()
  }

  // Смена тарифа подписки: бэк отменяет текущую Overpay-подписку и создаёт
  // новую на выбранный тариф → redirect на страницу оплаты (карта вводится
  // заново — платёжка не умеет переносить карту между подписками).
  const handleChangeSubscription = async () => {
    if (!selectedTariff || !user) return
    reachGoal('change_tariff_subscription', { tariff: selectedTariff.id, amount: selectedTariff.price })
    setChangeBusy(true)
    setChangeError('')
    try {
      const res = await api.request<{ ok: boolean; redirect_url: string; order_id?: string }>(
        '/api/v1/payments/subscription/change',
        { method: 'POST', body: { tariff_id: selectedTariff.id } },
      )
      // СБП-подписка приходит ссылкой привязки счёта (sub.nspk.ru): её рисуем
      // QR-кодом и ждём подтверждения в банке. Карточная форма — переходом.
      if (isNspk(res.redirect_url) && res.order_id) {
        setPayType('subscription')
        setPaymentUrl(res.redirect_url)
        setModalStep('qr')
        startPolling(res.order_id)
        setChangeBusy(false)
        return
      }
      markPaymentStart({ balanceBefore: user.credits_balance, tariffBefore: currentTariffId, tariffWanted: selectedTariff.id })
      window.location.href = res.redirect_url
    } catch (e: unknown) {
      setChangeError(e instanceof Error ? e.message : 'Не удалось начать смену тарифа')
      setChangeBusy(false)
    }
  }

  // Оплаченный период (разовый пакет или подписка, даже отменённая) ещё идёт — подписку на такой же или более дешёвый
  // тариф сервер не оформит (payments.create, гард «сценарии 1/3»). Тот же расчёт здесь, до оплаты: вместо отказа
  // «Ошибка» — окно выбора «повысить тариф / докупить токены» (владелец 16.10: «тупик без мотивации перейти дальше»).
  const paidPeriodTariff = (): Tariff | null => {
    const until = user?.subscription_expires_at
    if (!until || !currentTariffId || parseUtc(until).getTime() <= Date.now()) return null
    return tariffs.find((x) => x.id === currentTariffId) ?? null
  }

  const handlePay = async () => {
    if (!selectedTariff || !user || paying) return
    const useCard = recurrentEnabled && payType === 'subscription'
    // Гард: подписка без явного согласия невозможна (кнопка и так disabled)
    if (useCard && !subConsent) return
    // Промокод ещё проверяется — ждём расчёт (кнопка и так disabled)
    if (promo.pending) return
    if (useCard) {
      const paid = paidPeriodTariff()
      if (paid && selectedTariff.price <= paid.price) {
        setModalStep('paid_period')
        return
      }
    }
    // Код уходит только применённым; сервер пересчитает и зарезервирует его,
    // неподходящий вернёт 400 с текстом — покажем как ошибку платежа.
    const promoCode = promoQuote?.code ?? null
    reachGoal(useCard ? 'pay_tariff_subscription' : 'pay_tariff', { tariff: selectedTariff.id, amount: promoQuote?.price_after ?? selectedTariff.price })
    setPaying(true)
    setModalStep('loading')
    setErrorMsg('')
    try {
      const res = await api.request<PaymentCreateResponse>('/api/v1/payments/create', {
        method: 'POST',
        body: {
          amount: selectedTariff.price,
          credits: selectedTariff.credits,
          email: user.email || 'noemail@placeholder.com',
          telegram_id: user.telegram_id || null,
          currency: 'RUB',
          tariff_id: selectedTariff.id,
          recurrent: useCard,
          ...(promoCode ? { promo_code: promoCode } : {}),
        }
      })
      if (promoCode) clearStoredPromo(promoCode)
      setPayAmount(typeof res.amount === 'number' ? res.amount : null)
      if (useCard) {
        // Подписка через СБП: касса отдаёт ссылку привязки счёта (sub.nspk.ru).
        // Показываем её QR-кодом ровно как разовую оплату — клиент подтверждает
        // в банке привязку и первый платёж, статус ловим опросом.
        if (isNspk(res.payment_url)) {
          setPaymentUrl(res.payment_url)
          setModalStep('qr')
          startPolling(res.order_id)
          setPaying(false)
          return
        }
        // Карточная форма (фоллбэк кассы) — уходим на страницу оплаты,
        // paying не сбрасываем (страница меняется).
        markPaymentStart({ balanceBefore: user.credits_balance, tariffBefore: currentTariffId, tariffWanted: selectedTariff.id })
        window.location.href = res.payment_url
        return
      }
      if (!isNspk(res.payment_url)) {
        markPaymentStart({ balanceBefore: user.credits_balance, tariffBefore: currentTariffId, tariffWanted: selectedTariff.id })
        window.location.href = res.payment_url
        return
      }
      setPaymentUrl(res.payment_url)
      setModalStep('qr')
      startPolling(res.order_id)
      setPaying(false)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Ошибка создания платежа'
      setPaying(false)
      if (useCard && (e as { status?: number })?.status === 409 && /оплаченный период/i.test(msg)) {
        setModalStep('paid_period')
        return
      }
      setErrorMsg(msg)
      setModalStep('error')
    }
  }

  const closeModal = () => {
    stopPolling()
    setSelectedTariff(null)
    setPaymentUrl('')
    setErrorMsg('')
    setPayType('one_time')
    setSubConsent(false)
    setChangeError('')
    setChangeBusy(false)
    setPaying(false)
    setPayAmount(null)
    setModalStep('confirm')
  }

  // ── Рейл бонус-паков: какая карточка стоит на месте базы ──
  // Выбран пак → вариация базы в нём (нет вариации — база остаётся, как
  // Start в макете). Пак не выбран → своя вариация владельца замещает базу
  // (юзер видит именно свой тариф), остальные видят базы. Вариации никогда
  // не добавляются в сетку отдельными карточками.
  const ownedVariant = useMemo(
    () => tariffs.find((t) => t.pack_id && t.id === currentTariffId) ?? null,
    [tariffs, currentTariffId],
  )
  const visibleTariffs = useMemo(() => {
    const bases = tariffs.filter((t) => !t.pack_id)
    const shown = bases.map((base) => {
      if (selectedPack) {
        const inPack = tariffs.find(
          (v) => v.pack_id === selectedPack && v.base_tariff_id === base.id,
        )
        if (inPack) return inPack
      }
      if (!selectedPack && ownedVariant?.base_tariff_id === base.id) return ownedVariant
      return base
    })
    // База своей вариации деактивирована/пропала — тариф юзера всё равно виден.
    if (ownedVariant && !bases.some((b) => b.id === ownedVariant.base_tariff_id)) {
      shown.unshift(ownedVariant)
    }
    return shown
  }, [tariffs, selectedPack, ownedVariant])

  // Владелец вариации при заходе видит рейл на СВОЁМ паке (честная витрина
  // текущего состояния; выбор можно снять кликом).
  useEffect(() => {
    if (packInitRef.current || !ownedVariant?.pack_id || packs.length === 0) return
    packInitRef.current = true
    if (packs.some((p) => p.id === ownedVariant.pack_id)) setSelectedPack(ownedVariant.pack_id)
  }, [ownedVariant, packs])

  // «Выгодная» карточка: тариф с промо-бейджем из админки. Пока бейдж не
  // задан НИ У ОДНОГО тарифа — прежний хардкод (третья карточка), чтобы
  // деплой без настройки не снял бейдж с витрины. Задан хоть у одного —
  // управление полностью админское (выделяются все тарифы с бейджем).
  const hasAdminPromo = tariffs.some((t) => (t.promo_badge ?? '').trim().length > 0)
  const bestIndex = hasAdminPromo ? -1 : tariffs.length >= 3 ? 2 : -1

  // Off-состояние блока бесплатных нейросетей: «доступны с тарифа X», где X —
  // первый по порядку тариф, у которого бесплатные модели настроены. Если их
  // нет ни у кого — блок не показывается вовсе (нечего обещать).
  const firstFreeTariff = tariffs.find((t) => (t.free_models?.length ?? 0) > 0) ?? null

  // Тариф юзера (подписка ИЛИ разовый) — для бейджа «Ваш тариф».
  const ownedTariff = currentTariffId
    ? tariffs.find((t) => t.id === currentTariffId) ?? null
    : null
  // Тариф активной ПОДПИСКИ: всё, что дешевле него, мьютим и не даём
  // активировать (даунгрейд посреди оплаченного периода не продаём). Сравнение
  // по цене — устойчиво к порядку карточек; архивный тариф (нет в списке) или
  // отсутствие подписки (гость, разовый тариф, past_due, отменена) → ничего
  // не блокируем: разовый владелец волен купить любой тариф или подписку.
  const currentTariff = hasActiveSubscription ? ownedTariff : null

  if (loading) {
    return (
      <div className="tariffs-page">
        <div className="tariffs-loading"><div className="spinner" /></div>
      </div>
    )
  }

  // AggregateOffer из живых тарифов — цены в выдаче (rich snippet).
  const offersLd = tariffs.length
    ? JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Кредиты Umnik.AI',
        description: 'Пакеты кредитов для генерации в нейросетях',
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'RUB',
          lowPrice: String(Math.min(...tariffs.map((t) => t.price))),
          highPrice: String(Math.max(...tariffs.map((t) => t.price))),
          offerCount: String(tariffs.length),
          url: 'https://umnik.ai/tariffs',
        },
      }).replace(/</g, '\u003c')
    : null

  return (
    <div className="tariffs-page">
      {offersLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: offersLd }} />
      )}
      <button className="tariffs-close" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="tariffs-header">
        <h1 className="tariffs-title">Откройте максимум возможностей</h1>
      </div>

      {/* Переключатель линеек: пак — самостоятельное семейство тарифов, а не
          надбавка. Сегмент «Основные» = базовая сетка; сегмент пака подменяет
          карточки его вариациями (нет вариации — база остаётся). Всегда
          выбран ровно один сегмент. Пока бэк не отдал паки (мягкий запуск),
          переключателя нет. */}
      {packs.length > 0 && (() => {
        const activePack = packs.find((p) => p.id === selectedPack) ?? null
        return (
          <div className="tariffs-lanes">
            <div className="tariffs-lanes__seg" role="radiogroup" aria-label="Линейки тарифов">
              <button
                type="button"
                role="radio"
                aria-checked={!activePack}
                className={`tariffs-lanes__opt${!activePack ? ' is-on' : ''}`}
                onClick={() => setSelectedPack(null)}
              >
                Основные
              </button>
              {packs.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={activePack?.id === p.id}
                  className={`tariffs-lanes__opt${activePack?.id === p.id ? ' is-on' : ''}`}
                  onClick={() => setSelectedPack(p.id)}
                >
                  {p.name}
                </button>
              ))}
            </div>
            {activePack?.description ? (
              <p className="tariffs-lanes__desc">{activePack.description}</p>
            ) : null}
          </div>
        )
      })()}

      {/* key: смена линейки перемонтирует карточки — каскадная анимация
          появления читается как плавное «перелистывание» сетки */}
      <div className="tariffs-grid" key={selectedPack ?? 'base'}>
        {visibleTariffs.map((t, idx) => {
          const isBest = hasAdminPromo ? Boolean((t.promo_badge ?? '').trim()) : idx === bestIndex;
          const badgeText = (t.promo_badge ?? '').trim() || '-34% Выгодное предложение';
          const isFirst = idx === 0;
          // Бейдж «Ваш тариф» — и у подписки, и у разового тарифа; кнопка
          // «Управлять подпиской» — только у подписки (разовый владелец может
          // «Активировать» свой тариф повторно — продлить ещё на период)
          const isOwned = Boolean(ownedTariff && t.id === ownedTariff.id);
          // Вариация пака: разница с базой считается из пары карточек — цен
          // не вычисляем, только показываем данные обоих тарифов.
          const baseOf = t.base_tariff_id ? tariffs.find((x) => x.id === t.base_tariff_id) ?? null : null;
          const isCurrent = Boolean(currentTariff && t.id === currentTariff.id);
          const isLower = Boolean(currentTariff && t.price < currentTariff.price);
          // Текущий тариф не блокируем никогда (даже Start c start_used):
          // его кнопка — живая «Управлять подпиской» → кабинет
          const btnDisabled = !isCurrent && (isLower || (isFirst && startUsed));

          const handleToggle = () => {
            setOpenTariffIds(prev => {
              const next = new Set(prev);
              if (next.has(t.id)) next.delete(t.id);
              else next.add(t.id);
              return next;
            });
          };

          return (
            <TariffCardView
              key={t.id}
              style={{ '--ti': idx } as CSSProperties}
              tariff={t}
              baseOf={baseOf}
              isBest={isBest}
              badgeText={badgeText}
              isOwned={isOwned}
              isLower={isLower}
              buttonLabel={isCurrent
                ? 'Управлять подпиской'
                : isLower
                  ? 'Ниже вашего тарифа'
                  : isFirst && startUsed
                    ? 'Уже использован'
                    : hasActiveSubscription
                      ? 'Перейти на тариф'
                      : 'Активировать'}
              buttonDisabled={btnDisabled}
              buttonBest={isBest && !isCurrent}
              onButtonClick={() => {
                // Текущий тариф: не повторная покупка, а управление подпиской
                // (продление, докупка, отмена) в кабинете — паттерн Spotify/YT
                if (isCurrent) {
                  navigate('/account');
                  return;
                }
                handleSelect(t);
              }}
              recurrentEnabled={recurrentEnabled}
              collapsible={isMobile}
              isOpen={isMobile && openTariffIds.has(t.id)}
              onToggle={handleToggle}
              emptyFreeNote={firstFreeTariff ? `Доступны с тарифа ${firstFreeTariff.name}` : null}
            />
          );
        })}
      </div>

      {!user && (
        <div className="tariffs-login-hint">
          Уже есть аккаунт? <span onClick={() => openAuth('/login')}>Войти</span>
        </div>
      )}

      {selectedTariff && (
        <div className="pay-overlay" onClick={closeModal}>
          <div className="pay-modal" onClick={e => e.stopPropagation()}>
            <button className="pay-modal-close" onClick={closeModal}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {modalStep === 'confirm' && (
              <>
                <h2 className="pay-modal-title">Оформление заказа</h2>
                {!user && <p className="pay-modal-subtitle">Войдите в аккаунт, чтобы продолжить</p>}

                <div className="pay-order-info">
                  <div className="pay-order-row">
                    <span className="pay-order-label">Тариф</span>
                    <span className="pay-order-value">{selectedTariff.name}</span>
                  </div>
                  <div className="pay-order-row">
                    <span className="pay-order-label">Токены</span>
                    <span className="pay-order-value">
                      {(() => {
                        const oc = selectedTariff.credits_one_time
                        const oneOnly = (selectedTariff.purchase_mode ?? 'both') === 'one_time'
                        // Разовая оплата с другим пакетом: зачёркиваем
                        // подписочные токены — юзер видит смену, а не молчание.
                        if (!oneOnly && payType === 'one_time' && oc != null && oc !== selectedTariff.credits) return (
                          <>
                            <s className="pay-order-strike">{formatCredits(selectedTariff.credits)}</s>
                            {formatCredits(oc)} ток
                          </>
                        )
                        if (oneOnly) return <>{formatCredits(oc ?? selectedTariff.credits)} ток</>
                        return <>{formatCredits(selectedTariff.credits)} ток/мес</>
                      })()}
                      <Coin className="token-star" />
                    </span>
                  </div>
                  {/* Бонус по промокоду — сверху покупки, отдельной строкой */}
                  {promoQuote && promoQuote.bonus_credits > 0 && (
                    <div className="pay-order-row">
                      <span className="pay-order-label">По промокоду</span>
                      <span className="pay-order-value pay-order-value--promo">
                        +{formatCredits(promoQuote.bonus_credits)} ток
                        <Coin className="token-star" />
                      </span>
                    </div>
                  )}
                  <div className="pay-order-divider" />
                  <div className="pay-order-row total">
                    <span className="pay-order-label">К оплате</span>
                    <span className="pay-order-value">
                      {promoQuote && promoQuote.discount_rub > 0 ? (
                        <>
                          <s className="pay-order-strike">{promoQuote.price_before.toLocaleString('ru-RU')} ₽</s>
                          {promoQuote.price_after.toLocaleString('ru-RU')} ₽
                        </>
                      ) : (
                        <>{selectedTariff.price.toLocaleString('ru-RU')} ₽</>
                      )}
                    </span>
                  </div>
                </div>

                {/* ФЗ-376: сегмент-контрол «Подписка / Разовая оплата» —
                    акцентная пилюля переезжает между вариантами, неактивная
                    сторона = просто текст. Оба варианта на виду. Явное согласие
                    на автосписание — отдельный чекбокс ниже (пустой по умолчанию,
                    без него кнопка недоступна), не дефолтом. */}
                {recurrentEnabled && (selectedTariff.purchase_mode ?? 'both') === 'both' && (
                  <div
                    className={`pay-seg pay-seg--${payType}`}
                    role="tablist"
                    aria-label="Тип оплаты"
                  >
                    <span className="pay-seg__thumb" aria-hidden="true" />
                    <button
                      type="button"
                      role="tab"
                      aria-selected={payType === 'subscription'}
                      className="pay-seg__btn"
                      onClick={() => { setPayType('subscription'); setSubConsent(false) }}
                    >
                      Подписка
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={payType === 'one_time'}
                      className="pay-seg__btn"
                      onClick={() => { setPayType('one_time'); setSubConsent(false) }}
                    >
                      Разовая оплата
                    </button>
                  </div>
                )}

                {/* Промокод — после выбора типа оплаты: от него зависит,
                    подходит ли код. Гостю поле не показываем (проверка
                    требует входа). */}
                {user && <PromoCodeField promo={promo} disabled={paying} />}

                {recurrentEnabled && payType === 'subscription' ? (
                  <>
                    <div className="pay-sub-benefits">
                      <div className="pay-sub-benefit">
                        <img src="/icons/gray_check.svg" alt="" width="14" height="14" />
                        <span>Автопродление — доступ не прервётся</span>
                      </div>
                      <div className="pay-sub-benefit">
                        <img src="/icons/gray_check.svg" alt="" width="14" height="14" />
                        <span>
                          {(selectedTariff.token_expire_days ?? 30) >= 28 && (selectedTariff.token_expire_days ?? 30) <= 31
                            ? 'Списание ежемесячно — раз в 30 дней, до отмены'
                            : `Списание раз в ${selectedTariff.token_expire_days} дней, до отмены`}
                        </span>
                      </div>
                      {/* Перенос остатка — свой у тарифа; строку показываем
                          ТОЛЬКО когда перенос есть (иначе это не выгода) */}
                      {carryoverBenefit(selectedTariff) && (
                        <div className="pay-sub-benefit">
                          <img src="/icons/gray_check.svg" alt="" width="14" height="14" />
                          <span>{carryoverBenefit(selectedTariff)}</span>
                        </div>
                      )}
                      <div className="pay-sub-benefit">
                        <img src="/icons/gray_check.svg" alt="" width="14" height="14" />
                        <span>Отменить и отвязать карту — в любой момент</span>
                      </div>
                    </div>

                    <div className="pay-method">
                      <div className="pay-method-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
                        </svg>
                      </div>
                      <div className="pay-method-info">
                        <div className="pay-method-name">Банковская карта</div>
                        <div className="pay-method-desc">Автопродление подписки</div>
                      </div>
                    </div>

                    {/* Скидка подписки — только на первый платёж: продления по
                        полной цене, это видно до согласия на автосписание */}
                    {promoQuote && promoQuote.discount_rub > 0 && (
                      <div className="pay-promo-note">
                        Скидка на первый платёж, далее — {selectedTariff.price.toLocaleString('ru-RU')} ₽ за период
                      </div>
                    )}

                    {/* Явное согласие на автосписание — пустой обязательный чекбокс */}
                    <label className="pay-consent">
                      <input
                        type="checkbox"
                        checked={subConsent}
                        onChange={(e) => setSubConsent(e.target.checked)}
                      />
                      <span>
                        Я соглашаюсь на автопродление подписки и автоматическое списание{' '}
                        <strong>{selectedTariff.price.toLocaleString('ru-RU')} ₽</strong> с привязанной
                        карты — раз в {selectedTariff.token_expire_days ?? 30} дней, при каждом
                        продлении до отмены. Отменить и отвязать карту можно в любой момент:
                        Профиль → Подписка.
                      </span>
                    </label>

                    {/* Гейт согласия — только при реальной оплате (залогинен);
                        гостю кнопка просто ведёт на регистрацию */}
                    <button
                      className="pay-modal-btn primary"
                      disabled={paying || (Boolean(user) && (!subConsent || promo.pending))}
                      onClick={() => user ? handlePay() : openAuth('/register')}
                    >
                      {paying ? 'Создаём платёж…' : 'Оформить подписку'}
                    </button>

                    {!user && (
                      <button className="pay-modal-link" onClick={() => openAuth('/register')}>
                        Регистрация
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="pay-type-single">
                      Разовый платёж · пополнение токенов, без автопродления. Остаток токенов
                      сгорает через {selectedTariff.token_expire_days ?? 30} дней после пополнения.
                    </div>

                    {/* Апселл: что именно теряется без подписки — по данным
                        тарифа (перенос, подписочные бесплатные модели,
                        персонажи). Показываем только реальную разницу. */}
                    {(selectedTariff.purchase_mode ?? 'both') === 'both' && (() => {
                      const lost: string[] = []
                      const ocr = selectedTariff.credits_one_time
                      if (ocr != null && ocr < selectedTariff.credits) lost.push(`${formatCredits(selectedTariff.credits)} токенов вместо ${formatCredits(ocr)}`)
                      const co = selectedTariff.carryover_percent ?? 0
                      if (co > 0) lost.push(co >= 100 ? 'перенос всего остатка токенов' : `перенос ${co}% остатка токенов`)
                      const subFree = (selectedTariff.free_models ?? []).filter((m) => m.applies_to === 'subscription')
                      if (subFree.length) lost.push(`бесплатные нейросети: ${subFree.map((m) => m.name).join(', ')}`)
                      const subDisc = (selectedTariff.model_discounts ?? []).filter((d) => d.applies_to === 'subscription')
                      if (subDisc.length) lost.push(`скидки на ${subDisc.map((d) => d.name).join(', ')}`)
                      // Строки тарифа и кастом-боксы со скоупом «подписке»
                      const stripHtml = (x: string) => x.replace(/<[^>]+>/g, '').replace(/\*\*/g, '')
                      ;(selectedTariff.items ?? [])
                        .filter((i) => i.is_enabled && i.applies_to === 'subscription' && i.custom_description)
                        .forEach((i) => lost.push(stripHtml(i.custom_description!)))
                      ;(selectedTariff.card_blocks ?? [])
                        .filter((b) => b.kind === 'custom' && b.applies_to === 'subscription')
                        .forEach((b) => {
                          const label = b.title || b.payload?.lines?.[0]?.title || b.payload?.lines?.[0]?.text
                          if (label) lost.push(stripHtml(label))
                        })
                      const cl = selectedTariff.character_limit ?? 0
                      const clOt = selectedTariff.character_limit_one_time
                      if (typeof clOt === 'number' && clOt < cl) lost.push(`аватаров: ${clOt} вместо ${cl}`)
                      if (!lost.length) return null
                      return (
                        <div className="pay-onetime-lost">
                          <div className="pay-onetime-lost__head">Только по подписке:</div>
                          <ul>
                            {lost.map((l, i) => <li key={i}>{l}</li>)}
                          </ul>
                        </div>
                      )
                    })()}

                    <div className="pay-method">
                      <div className="pay-method-icon">
                        <img
                          src={document.documentElement.getAttribute('data-theme') === 'dark' ? '/icons/sbp_dark.svg' : '/icons/sbp_light.svg'}
                          alt="СБП"
                        />
                      </div>
                      <div className="pay-method-info">
                        <div className="pay-method-name">Onlypay - Оплата через СБП</div>
                        <div className="pay-method-desc">Система быстрых платежей, мгновенное зачисление</div>
                      </div>
                    </div>

                    <button
                      className="pay-modal-btn primary"
                      disabled={paying || promo.pending}
                      onClick={() => user ? handlePay() : openAuth('/register')}
                    >
                      {paying ? 'Создаём платёж…' : 'Оплатить разово'}
                    </button>

                    {!user && (
                      <button className="pay-modal-link" onClick={() => openAuth('/register')}>
                        Регистрация
                      </button>
                    )}
                  </>
                )}
              </>
            )}

            {/* Смена тарифа подписки — минимальный экран: только суть (тариф,
                токены, цена) + карта + одна ключевая мысль. Разовый платёж тут
                НЕ предлагаем: смена подписки всегда остаётся подпиской. */}
            {user && modalStep === 'change' && (
              <>
                <h2 className="pay-modal-title">Переход на тариф {selectedTariff.name}</h2>

                <div className="pay-order-info">
                  <div className="pay-order-row">
                    <span className="pay-order-label">Токены</span>
                    <span className="pay-order-value">
                      {formatCredits(selectedTariff.credits)} ток/мес
                      <Coin className="token-star" />
                    </span>
                  </div>
                  <div className="pay-order-divider" />
                  <div className="pay-order-row total">
                    <span className="pay-order-label">К оплате</span>
                    <span className="pay-order-value">{selectedTariff.price.toLocaleString('ru-RU')} ₽/мес</span>
                  </div>
                </div>

                <div className="pay-method">
                  <div className="pay-method-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                  </div>
                  <div className="pay-method-info">
                    <div className="pay-method-name">Банковская карта</div>
                    <div className="pay-method-desc">Автопродление по новому тарифу</div>
                  </div>
                </div>

                <div className="pay-change-hint">
                  Текущая подписка заменится только после оплаты. Не завершите — останется прежняя.
                </div>

                {changeError && <div className="pay-modal-text" style={{ color: '#f87171' }}>{changeError}</div>}

                <button
                  className="pay-modal-btn primary"
                  onClick={handleChangeSubscription}
                  disabled={changeBusy}
                >
                  {changeBusy ? 'Переход к оплате...' : 'Перейти на тариф'}
                </button>
                <button className="pay-modal-link" onClick={closeModal} disabled={changeBusy}>
                  Отмена
                </button>
              </>
            )}

            {user && modalStep === 'loading' && (
              <div className="pay-loading">
                <div className="spinner" />
                <p>Переход к оплате...</p>
              </div>
            )}

            {user && modalStep === 'qr' && (
              <>
                <h2 className="pay-modal-title">
                  {payType === 'subscription' ? 'Подписка через СБП' : 'Оплата через СБП'}
                </h2>
                <p className="pay-qr-hint">
                  {payType === 'subscription'
                    ? 'Отсканируйте QR-код в приложении банка — оно попросит подтвердить привязку счёта и первый платёж'
                    : 'Отсканируйте QR-код в приложении вашего банка'}
                </p>
                <div className="pay-qr-row">
                <div className="wrapper">
                  <div className="pay-qr-info">
                    <div className="pay-qr-provider">OnlyPay, Kassa ai</div>
                    <div className="pay-qr-amount">{(payAmount ?? selectedTariff.price).toLocaleString('ru-RU')} ₽</div>
                  </div>
                  <div className="pay-qr-status">
                      <div className="pay-qr-spinner" />
                      <span>Ожидание оплаты</span>
                    </div>
                  </div>
                  <div className="pay-qr-wrapper">
                    <QRCodeSVG value={paymentUrl} size={125} bgColor="#ffffff" fgColor="#000000" level="M" includeMargin={false} />
                  </div>
                </div>
                <a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="pay-modal-btn primary">
                  Открыть в телефоне
                </a>
                <button className="pay-modal-link" onClick={closeModal}>Отменить</button>
              </>
            )}

            {user && modalStep === 'success' && (
              <>
                <div className="pay-modal-icon success">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" />
                  </svg>
                </div>
                <h2 className="pay-modal-title">Оплата получена!</h2>
                <p className="pay-modal-text">На ваш баланс начислено <strong>{formatCredits(creditsAdded)}</strong> токенов</p>
                {/* QR-флоу = всегда разовый платёж (подписка уходит на redirect) —
                    говорим честно, что именно куплено */}
                <p className="pay-modal-text" style={{ opacity: 0.75 }}>
                  Разовый платёж: тариф «{selectedTariff.name}» активирован на один период, без автопродления
                </p>
                <button className="pay-modal-btn primary" onClick={() => { closeModal(); navigate('/account') }}>
                  Перейти в аккаунт
                </button>
              </>
            )}

            {user && modalStep === 'paid_period' && selectedTariff && (() => {
              // Следующий тариф дороже — из тех же карточек, что видит человек; выше некуда — только докупка токенов.
              const ref = ownedTariff ?? selectedTariff
              const higher = visibleTariffs
                .filter((x) => x.price > ref.price)
                .sort((a, b) => a.price - b.price)[0] ?? null
              const until = user.subscription_expires_at ? formatPaidUntil(user.subscription_expires_at) : null
              const monthly = higher ? higher.purchase_mode !== 'one_time' : false
              const buyTokens = () => {
                closeModal()
                if (!openTopUp(0)) navigate('/account')
              }
              return (
                <>
                  <div className="pay-modal-icon info">
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10" /><path d="M12 11v5" /><circle cx="12" cy="7.6" r="0.6" fill="currentColor" />
                    </svg>
                  </div>
                  <h2 className="pay-modal-title">
                    {ownedTariff ? `Тариф «${ownedTariff.name}» уже оплачен${until ? ` до ${until}` : ''}` : 'У вас уже есть оплаченный период'}
                  </h2>
                  <p className="pay-modal-text">
                    Подписку на такой же или более дешёвый тариф можно оформить после окончания периода. А сейчас можно:
                  </p>
                  <div className="pay-choice">
                    {higher && (
                      <div className="pay-choice__opt pay-choice__opt--main">
                        <span className="pay-choice__kicker">Повысить тариф</span>
                        <div className="pay-choice__row">
                          <span className="pay-choice__name">{higher.name}</span>
                          <span className="pay-choice__price">{higher.price.toLocaleString('ru-RU')} ₽{monthly && <small> / мес</small>}</span>
                        </div>
                        <span className="pay-choice__sub">
                          {/* Сравнение тарифов, а не «у вас сейчас»: у разового пакета токенов бывает иначе, чем у подписки */}
                          {formatCredits(higher.credits)} токенов{monthly ? ' в месяц' : ''}
                          {ownedTariff ? ` против ${formatCredits(ownedTariff.credits)} у ${ownedTariff.name}` : ''}
                        </span>
                        <button className="pay-modal-btn primary" onClick={() => handleSelect(higher)}>Перейти на {higher.name}</button>
                      </div>
                    )}
                    <div className={`pay-choice__opt${higher ? '' : ' pay-choice__opt--main'}`}>
                      <span className="pay-choice__kicker">Докупить токены</span>
                      <span className="pay-choice__sub">
                        Тариф остаётся{until ? ` до ${until}` : ''}, токены добавятся к балансу сразу.
                      </span>
                      <button className={`pay-modal-btn ${higher ? 'secondary' : 'primary'}`} onClick={buyTokens}>Докупить токены</button>
                    </div>
                  </div>
                  <button className="pay-choice__close" onClick={closeModal}>Закрыть</button>
                </>
              )
            })()}

            {user && modalStep === 'error' && (
              <>
                <div className="pay-modal-icon error">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <h2 className="pay-modal-title">Ошибка</h2>
                <p className="pay-modal-text">{errorMsg}</p>
                <button className="pay-modal-btn primary" onClick={() => setModalStep('confirm')}>Попробовать снова</button>
                <button className="pay-modal-btn secondary" onClick={closeModal}>Закрыть</button>
              </>
            )}

            {user && modalStep === 'timeout' && (
              <>
                <div className="pay-modal-icon error">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <h2 className="pay-modal-title">Время ожидания истекло</h2>
                <p className="pay-modal-text">Платёж не был получен в течение 5 минут. Если вы оплатили — токены будут начислены автоматически.</p>
                <button className="pay-modal-btn primary" onClick={() => setModalStep('confirm')}>Попробовать снова</button>
                <button className="pay-modal-btn secondary" onClick={closeModal}>Закрыть</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

